/*
 * Copyright (c) 2026 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, July 5th 2026, 3:38:30 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { Stack, Switch, FormControl, FormControlLabel, TextField } from "@mui/material";
import { COLORSCHEME } from "theme/styles";
import { HelperText, Typography, EmphasisText, OSAccordion } from "ui-widgets";

const isBasicExpression = (expression: string) => {
  return !expression || (expression.startsWith("PT") && expression.endsWith("H"));
};

export const ISO8601IntervalInput = ({
  label,
  value = "",
  setValue,
}: {
  label: string;
  setValue: (value: any) => void;
  value: string;
}) => {
  const isAdvancedExpression = !isBasicExpression(value as string);
  const onChangeAdvanceSwitch = (
    event: Parameters<React.ComponentProps<typeof Switch>["onChange"]>[0],
    checked: Parameters<React.ComponentProps<typeof Switch>["onChange"]>[1]
  ) => {
    if (checked) {
      const defaultValue = isBasicExpression(value) ? "R4/PT1H" : value;
      return setValue(defaultValue as any);
    }
    const defaultValue = isBasicExpression(value) ? value : "PT1H";
    setValue(defaultValue as any);
  };
  const basicHourValue = isBasicExpression(value as any) ? value.replace("PT", "").replace("H", "") : "";
  const onChangeBasicHours = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(`PT${event.target.value}H` as any);
  };

  return (
    <Stack rowGap={2}>
      <Typography variant="body1">{label}</Typography>
      <FormControl>
        <FormControlLabel
          control={<Switch onChange={onChangeAdvanceSwitch} checked={isAdvancedExpression} />}
          label="Use advanced ISO 8601 Interval expression?"
        />
      </FormControl>
      {isAdvancedExpression ? (
        <>
          <TextField label={label} value={value} onChange={(event) => setValue(event.target.value)} />
          <ISO8601ExpressionsHelpAccordion />
        </>
      ) : (
        <>
          <Stack direction="row" columnGap={1} alignItems={"center"} justifyContent={"flex-start"}>
            <Typography fontSize={16}>After</Typography>
            <TextField
              style={{ width: "50px" }}
              variant="standard"
              defaultValue={basicHourValue}
              onChange={onChangeBasicHours}
              inputProps={{ style: { textAlign: "center" } }}
            />
            <Typography fontSize={16}>Hours</Typography>
          </Stack>
        </>
      )}
    </Stack>
  );
};

const ISO8601ExpressionsHelpAccordion = () => (
  <OSAccordion
    mainSx={{ background: "rgba(var(--primary), 5%)" }}
    heading={<EmphasisText>Learn more about ISO 8601 time interval expressions</EmphasisText>}
  >
    <HelperText>
      Enter one of the following:
      <ul>
        <li>
          A valid ISO 8601 duration expression (
          <a href="https://iso8601.com/#duration" target="_blank" rel="noreferrer">
            Learn more
          </a>
          )
        </li>
        <li>
          A valid ISO 8601 Time Interval expression (
          <a href="https://iso8601.com/#intervals" target="_blank" rel="noreferrer">
            Learn more
          </a>
          ). ISO 8601 time intervals can be expressed in four ways:
          <ul>
            <li>
              {"<start>/<end>"} where each value must be a valid ISO 8601 date e.g.
              2007-03-01T13:00:00Z/2008-05-11T15:30:00Z
            </li>
            <li>
              {"<start>/<duration>"} where the start is a valid ISO 8601 date and duration is a valid ISO 8601 duration
              expression e.g. "2007-03-01T13:00:00Z/PT1H" denotes 1 hour after 1pm on 3rd March 2007 i.e. 2pm on that
              date
            </li>
            <li>
              {"<duration>/<end>"} where the duration is a valid ISO 8601 duration expression and end is a valid ISO
              8601 date e.g. "PT1H/2007-03-01T13:00:00Z" denotes 1 hour before 1pm on 3rd March 2007 i.e. 12pm on that
              date
            </li>
            <li>
              {"<duration>"} where the duration is a valid ISO 8601 duration expression. Note that if you provide a
              duration only, the start of the count will be from the task creation, assingment or started date depending
              on the option you select in the 'Count from' field e.g. "PT1M" will denote a duration of 1 Month after the
              task creation date.
            </li>
          </ul>
        </li>
      </ul>
      You can also make your expression more advanced by defining a recurring expression to configure recurring events(
      <a href="https://iso8601.com/#intervals" target="_blank" rel="noreferrer">
        Learn more
      </a>
      ). Simply prefix the interval with R followed by an optional count. If no count is given, the recurrence is
      unbounded (repeats forever). For example:
      <ul>
        <li>
          "R2/PT1H" will fire the event 1 hour after the task creation date and repeat it 2 more times, 1 hour apart,
          resulting in 3 total events being fired.{" "}
        </li>
        <li>
          "R/PT1H" will fire the event 1 hour after the task creation date and repeat it an unlimited number of times, 1
          hour apart, resulting in an infinite total events being fired. . Note to stop an unlimited event loop thats
          already in progress, you have to reconfigure this expression here and it will apply to all process instances
          affected.
        </li>
        <li>
          "R2/2026-01-01T00:00:00/PT1H" will fire the event 1 hour after midnight of 2026-01-01 and repeat it 2 more
          times, 1 hour apart, resulting in 3 total events being fired.{" "}
        </li>
        <li>
          "R2/PT1H/2026-01-01T00:00:00" will fire the event 1 hour before midnight of 2026-01-01 and repeat it 2 more
          times, 1 hour apart, resulting in 3 total events being fired.{" "}
        </li>
      </ul>
      <EmphasisText color={COLORSCHEME.secondary}>
        <Typography>IMPORTANT NOTE:</Typography>
      </EmphasisText>
      <EmphasisText color={COLORSCHEME.secondary}>
        To configure a scheduled event for a recurring point in time (e.g. everyday at midnight, every Tuesday at 10am),
        kindly use a cron expression instead of an ISO 8601 expression. ISO 8601 timer intervals are best suited for
        expressing durations and periods, while cron expressions shine for expressing points in time.
      </EmphasisText>
    </HelperText>
  </OSAccordion>
);
